import produce from 'immer';
import { Language } from '@/__generated__/globalTypes';
import { initialRecurringState } from '@/types/common';
import {
  ProviderMVAction,
  ProviderMVState,
  ProviderFreeGatedDefaultRates,
} from '@/types/providerMV';

export const initialState: ProviderMVState = {
  city: '',
  state: '',
  zipcode: '',
  distance: 15,
  lat: null,
  lng: null,
  initialAccountCreationAttempted: false,
  caregiverPreferencesPersisted: false,
  firstName: '',
  lastName: '',
  headline: '',
  hourlyChildCareRate: ProviderFreeGatedDefaultRates.MIN_RATE,
  hourlySeniorCareRate: ProviderFreeGatedDefaultRates.MIN_RATE,
  hourlyTutoringRate: ProviderFreeGatedDefaultRates.MIN_RATE,
  hourlyHousekeepingRate: ProviderFreeGatedDefaultRates.MIN_RATE,
  hourlyPetCareRate: ProviderFreeGatedDefaultRates.MIN_RATE,
  providerPayRange: [],
  ishourlyHousekeepingRateUpdated: false,
  ishourlySeniorCareRateUpdate: false,
  ishourlyTutoringRateUpdated: false,
  bio: '',
  recurringJob: false,
  oneTime: false,
  defaultRatePerChild: [0],
  defaultRecurringHourlyRate: [0, 0],
  min: '',
  max: '',
  hours: '',
  numberOfJobsNear: 0,
  jobTypes: [],
  jobTypesSchedules: {},
  recurring: { ...initialRecurringState.recurring },
  languages: [Language.ENGLISH],
  education: '',
  selectedAboutMe: ['smokes'],
  selectedHelpWith: [],
  selectedSkills: [],
  experienceYears: 1,
  numberOfChildren: 1,
  ageGroups: [],
  callProviderNameUpdateMutation: true,
  textAnalysisValidationId: '',
  serviceType: 'CHILDCARE',
  secondaryVertical: [],
  pets: [
    {
      name: 'Dog',
      isSelected: false,
    },
    {
      name: 'Cat',
      isSelected: false,
    },
  ],
};

export const reducer = produce((initialDraftState: ProviderMVState, action: ProviderMVAction) => {
  const draft = { ...initialDraftState };
  switch (action.type) {
    case 'setProviderMVLocation':
      draft.zipcode = action.zipcode;
      draft.city = action.city;
      draft.state = action.state;
      draft.lat = action.lat;
      draft.lng = action.lng;
      return draft;

    case 'setProviderMVDistance':
      draft.distance = action.distance;
      return draft;

    case 'setProviderMVInitialAccountCreationAttempted':
      draft.initialAccountCreationAttempted = action.initialAccountCreationAttempted;
      return draft;

    case 'setProviderMVCaregiverPreferencesPersisted':
      draft.caregiverPreferencesPersisted = action.caregiverPreferencesPersisted;
      return draft;

    case 'setRecurringJob':
      draft.recurringJob = action.recurringJob;
      return draft;

    case 'setOneTime':
      draft.oneTime = action.oneTime;
      return draft;

    case 'setDefaultRatePerChild':
      draft.defaultRatePerChild = action.defaultRatePerChild;
      return draft;

    case 'setDefaultRecurringHourlyRate':
      draft.defaultRecurringHourlyRate = action.defaultRecurringHourlyRate;
      return draft;

    case 'setProviderMVRecurringData':
      draft.recurring = action.data;
      return draft;

    case 'setInputValuesForJobTypes':
      draft.min = action.min;
      draft.max = action.max;
      draft.hours = action.hours;
      return draft;

    case 'setMVFirstName':
      draft.firstName = action.firstName;
      return draft;

    case 'setMVLastName':
      draft.lastName = action.lastName;
      return draft;

    case 'setMVHeadline':
      draft.headline = action.headline;
      return draft;

    case 'setMVBio':
      draft.bio = action.bio;
      return draft;

    case 'setMVChildCareHourlyRate':
      draft.hourlyChildCareRate = action.hourlyChildCareRate;
      return draft;

    case 'setMVSeniorCareHourlyRate':
      draft.hourlySeniorCareRate = action.hourlySeniorCareRate;
      return draft;

    case 'setMVTutoringHourlyRate':
      draft.hourlyTutoringRate = action.hourlyTutoringRate;
      return draft;

    case 'setMVHousekeepingHourlyRate':
      draft.hourlyHousekeepingRate = action.hourlyHousekeepingRate;
      return draft;

    case 'setMVPetCareHourlyRate':
      draft.hourlyPetCareRate = action.hourlyPetcareRate;
      return draft;

    case 'setMVProviderPayRange':
      draft.providerPayRange = action.providerPayRange;
      return draft;

    case 'setMVHousekeepingRateUpdated':
      draft.ishourlyHousekeepingRateUpdated = action.ishourlyHousekeepingRateUpdated;
      return draft;
    case 'setMVSeniorCareRateUpdated':
      draft.ishourlySeniorCareRateUpdate = action.ishourlySeniorCareRateUpdated;
      return draft;
    case 'setMVTutoringRateUpdated':
      draft.ishourlyTutoringRateUpdated = action.ishourlyTutoringRateUpdated;
      return draft;

    case 'setNumberOfJobsNear':
      draft.numberOfJobsNear = action.numberOfJobsNear;
      return draft;

    case 'setProviderMVLanguages':
      draft.languages = action.languages;
      return draft;

    case 'setProviderMVEducation':
      draft.education = action.education;
      return draft;

    case 'setProviderMVSelectedAboutMe':
      draft.selectedAboutMe = action.selectedAboutMe;
      return draft;

    case 'setProviderMVSelectedHelpWith':
      draft.selectedHelpWith = action.selectedHelpWith;
      return draft;

    case 'setProviderMVSelectedSkills':
      draft.selectedSkills = action.selectedSkills;
      return draft;

    case 'setProviderMVExperienceYears':
      draft.experienceYears = action.experienceYears;
      return draft;

    case 'setProviderMVAgegroups':
      draft.ageGroups = action.ageGroups;
      return draft;

    case 'setProviderMVComfortableCaringFor':
      draft.numberOfChildren = action.numberOfChildren;
      return draft;

    case 'setCallProviderNameUpdateMutation':
      draft.callProviderNameUpdateMutation = action.callProviderNameUpdateMutation;
      return draft;

    case 'setTextAnalysisValidationId':
      draft.textAnalysisValidationId = action.textAnalysisValidationId;
      return draft;

    case 'setProviderMVServiceType':
      draft.serviceType = action.serviceType;
      return draft;

    case 'setProviderMVSecondaryVertical':
      draft.secondaryVertical = action.secondaryVertical;
      return draft;

    case 'updatePetObject':
      draft.pets = action.value;
      return draft;

    case 'setProviderMVZipCode':
      draft.zipcode = action.value;
      return draft;

    default:
      return draft;
  }
});
